// src/components/CohortManagement.jsx
// Cohort management for head marker

import React, { useState, useEffect } from 'react';
import { ArrowLeft, Check, X, Trash2, Undo2, Users, Phone, FileText, CheckCircle, ChevronRight, ChevronDown, UserCheck, Ban, RefreshCw, FolderDown, Archive, Pencil, AlertTriangle, Clock, Calendar, Settings as SettingsIcon } from 'lucide-react';
import {
  getAllCohorts,
  createCohort,
  updateCohort,
  softDeleteCohort,
  restoreCohort,
  permanentlyDeleteCohortNow,
  getRecentlyDeletedCohorts,
  COHORT_DELETION_RETENTION_DAYS,
  generateCohortCode,
  getRecentRegistrations,
  approveRegistration,
  deleteStudentAccount,
  getRecentlyDeletedStudents,
  restoreDeletedStudent,
  getCohortStudentCounts,
  isStudentLinkedToOtherTutors,
  STUDENT_DELETION_RETENTION_DAYS,
  getCohortStudents,
  getStudentMarkedSubmissions,
  suspendStudentAccount,
  reactivateStudentAccount,
  moveStudentToCohort,
  getSuspendedStudents,
  archiveStudentData,
  updateStudentName,
  setStudentExtraTime,
  clearStudentExtraTime
} from '../firebase/cohorts';
import { getHomeworkNames, getMaxMarks } from '../firebase/settings';
import { getAuthenticatedFileUrl, permanentlyDeleteSubmission, sendMarkedBackToMarker } from '../firebase/submissions';
import { getMarker } from '../firebase/users';
import { CLOUD_FUNCTION_URL, notificationRequest } from '../firebase/headMarkerNotifications';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getApp } from 'firebase/app';
import { getAuth } from 'firebase/auth';
import ResultsView from './ResultsView';
import DeadlineManager from '../DeadlineManager';
import useIsMobile from '../hooks/useIsMobile';

// This site's own public address, for the copyable registration link —
// the staging build overrides it (see .env.staging).
const PUBLIC_URL = process.env.REACT_APP_PUBLIC_URL || 'https://homework-solutions.web.app';

// The Cohorts tab. Opens on the current cohort's results grid (the "hub"):
// cohort details and code at the top, student names open that student's
// page (homeworks + Archive/Suspend/Extra Time/Move/Delete + edit name), and
// the cohort's deadlines are a button away. Everything occasional - creating
// and deleting cohorts, suspended and recently deleted students, bulk
// deletes - is behind "Manage cohorts" (the original Cohorts screen).
//   orgId                - for the results grid's remembered cohort
//   renderCohortActions  - extra toolbar buttons for a cohort (Sync to Sheets)
//   renderDeadlinesExtra - shown under a cohort's deadlines (reminder emails)
function CohortManagement({ onCohortChange, orgId, renderCohortActions, renderDeadlinesExtra }) {
  const isMobile = useIsMobile();
  const [screen, setScreen] = useState('hub'); // 'hub' | 'manage'
  const [hubCohort, setHubCohort] = useState(null);
  const [showHubDeadlines, setShowHubDeadlines] = useState(false);
  const [studentReturnTo, setStudentReturnTo] = useState('list'); // 'hub' | 'list'
  const [showRegistrations, setShowRegistrations] = useState(false);
  const [cohorts, setCohorts] = useState([]);
  const [recentlyDeletedCohorts, setRecentlyDeletedCohorts] = useState([]);
  const [showRecentlyDeleted, setShowRecentlyDeleted] = useState(false);
  const [recentlyDeletedStudents, setRecentlyDeletedStudents] = useState([]);
  const [cohortStudentCounts, setCohortStudentCounts] = useState({});
  const [showRecentlyDeletedStudents, setShowRecentlyDeletedStudents] = useState(false);
  const [recentRegistrations, setRecentRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    startDate: '',
    endDate: ''
  });
  const [error, setError] = useState('');
  
  // Archive view state
  const [viewingCohort, setViewingCohort] = useState(null);
  const [cohortStudents, setCohortStudents] = useState([]);
  const [viewingStudent, setViewingStudent] = useState(null);
  const [studentSubmissions, setStudentSubmissions] = useState([]);
  const [homeworkNames, setHomeworkNames] = useState({});
  const [maxMarks, setMaxMarks] = useState({});
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [loadingSubmissions, setLoadingSubmissions] = useState(false);
  
  // Inline name editing state
  const [editingNameId, setEditingNameId] = useState(null);
  const [editingNameValue, setEditingNameValue] = useState('');

  // Student management state
  const [currentView, setCurrentView] = useState('cohorts'); // 'cohorts', 'suspended'
  const [suspendedStudents, setSuspendedStudents] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [showSuspendDialog, setShowSuspendDialog] = useState(false);
  const [showExtraTimeDialog, setShowExtraTimeDialog] = useState(false);
  const [extraTimeType, setExtraTimeType] = useState('fixed');
  const [extraTimeValue, setExtraTimeValue] = useState('');
  const [showMoveDialog, setShowMoveDialog] = useState(false);
  const [showArchiveDialog, setShowArchiveDialog] = useState(false);
  const [suspensionReason, setSuspensionReason] = useState('');
  const [targetCohortId, setTargetCohortId] = useState('');
  const [archiveData, setArchiveData] = useState(null);
  const [actionInProgress, setActionInProgress] = useState(false);
  
  // Bulk selection state
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [showBulkDeleteDialog, setShowBulkDeleteDialog] = useState(false);
  const [showDeleteAllDialog, setShowDeleteAllDialog] = useState(false);
  const [copiedCohortCode, setCopiedCohortCode] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (!viewingStudent) return;
    const updated = cohortStudents.find(s => s.id === viewingStudent.id);
    if (updated) setViewingStudent(updated);
  }, [cohortStudents]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadData = async () => {
    setLoading(true);
    try {
      const [cohortsData, registrationsData, names, suspendedData, marks, deletedData, deletedStudentsData] = await Promise.all([
        getAllCohorts(),
        getRecentRegistrations(),
        getHomeworkNames(),
        getSuspendedStudents(),
        getMaxMarks(),
        getRecentlyDeletedCohorts(),
        getRecentlyDeletedStudents().catch(err => { console.error('Recently deleted students error:', err); return []; })
      ]);
      setCohorts(cohortsData);
      setRecentRegistrations(registrationsData);
      setHomeworkNames(names);
      setSuspendedStudents(suspendedData);
      setMaxMarks(marks);
      setRecentlyDeletedCohorts(deletedData);
      setRecentlyDeletedStudents(deletedStudentsData);
      // Counts load separately so a slow linked-students lookup can't hold up the page
      getCohortStudentCounts()
        .then(setCohortStudentCounts)
        .catch(err => console.error('Cohort student counts error:', err));
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleNameChange = (e) => {
    const name = e.target.value;
    const suggestedCode = generateCohortCode(name);
    setFormData({
      ...formData,
      name,
      code: suggestedCode
    });
  };

  const handleCreateCohort = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.code) {
      setError('Please enter cohort name and code');
      return;
    }
    
    setError('');
    
    try {
      await createCohort(formData);
      alert(`Cohort created successfully!\n\nShare this code with students: ${formData.code}`);
      setShowCreateForm(false);
      setFormData({ name: '', code: '', startDate: '', endDate: '' });
      loadData();
      // Notify parent to refresh cohorts list
      if (onCohortChange) onCohortChange();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleToggleActive = async (cohortId, currentStatus) => {
    try {
      await updateCohort(cohortId, { active: !currentStatus });
      loadData();
    } catch (error) {
      alert('Failed to update cohort');
    }
  };

  const handleDeleteCohort = async (cohortId, cohortName) => {
    const typed = window.prompt(
      `This will move "${cohortName}" to Recently Deleted. Its student profiles, submissions, and PDF files are kept untouched for ${COHORT_DELETION_RETENTION_DAYS} days, during which you can restore it - after that it's permanently deleted.\n\nType the cohort name to confirm:`
    );
    if (typed === null) return; // cancelled
    if (typed.trim() !== cohortName.trim()) {
      alert('Cohort name did not match. Deletion cancelled.');
      return;
    }

    try {
      await softDeleteCohort(cohortId);
      alert(`"${cohortName}" moved to Recently Deleted. You can restore it any time in the next ${COHORT_DELETION_RETENTION_DAYS} days.`);
      loadData();
    } catch (err) {
      alert(err.message);
    }
  };

  const handleRestoreCohort = async (cohortId, cohortName) => {
    try {
      await restoreCohort(cohortId);
      alert(`"${cohortName}" restored.`);
      loadData();
    } catch (err) {
      alert(err.message);
    }
  };

  const handlePermanentlyDeleteCohort = async (cohortId, cohortName) => {
    const typed = window.prompt(
      `This will IMMEDIATELY and PERMANENTLY delete "${cohortName}" - all student profiles, submissions, and PDF files. This cannot be undone.\n\nType the cohort name to confirm:`
    );
    if (typed === null) return; // cancelled
    if (typed.trim() !== cohortName.trim()) {
      alert('Cohort name did not match. Deletion cancelled.');
      return;
    }

    try {
      await permanentlyDeleteCohortNow(cohortId);
      alert(`"${cohortName}" permanently deleted.`);
      loadData();
    } catch (err) {
      alert(err.message);
    }
  };

  const handleApproveRegistration = async (studentId, studentName) => {
    try {
      await approveRegistration(studentId);

      // Also force-verify their email so they can log in
      // even if they haven't clicked the verification link
      try {
        const auth = getAuth();
        const callerUid = auth.currentUser?.uid;
        if (callerUid) {
          const functions = getFunctions(getApp());
          const verifyEmail = httpsCallable(functions, 'verifyStudentEmail');
          await verifyEmail({ studentId, callerUid });
        }
      } catch (verifyError) {
        console.error('Email verification failed (non-blocking):', verifyError);
      }

      alert(`${studentName} has been approved and their email has been verified. They can now log in.`);
      loadData();
    } catch (error) {
      alert('Failed to approve registration');
    }
  };

  const handleDeleteStudent = async (studentId, studentName, studentEmail) => {
    let linked = false;
    try {
      linked = await isStudentLinkedToOtherTutors(studentId);
    } catch (err) {
      console.error('Linked student check error:', err);
    }
    const confirmed = window.confirm(linked
      ? `Remove ${studentName} from your organisation?\n\n${studentName} also studies with another tutor, so their account is kept for that tutor. They'll be removed from your organisation straight away, and all their work with you (submissions, marked scripts and exam answers) will be permanently deleted.\n\nThis can't be restored from Recently Deleted Students.`
      : `Delete ${studentName}?\n\nThey'll be locked out straight away and moved to Recently Deleted Students. You can restore them for ${STUDENT_DELETION_RETENTION_DAYS} days - after that, their account and all their work are permanently deleted.`
    );
    
    if (!confirmed) return;
    
    try {
      await deleteStudentAccount(studentId, studentEmail);
      alert(linked
        ? `${studentName} has been removed from your organisation`
        : `${studentName} has been moved to Recently Deleted Students`);
      if (viewingStudent?.id === studentId) leaveStudentPage();
      
      // Reload data
      if (viewingCohort) {
        const students = await getCohortStudents(viewingCohort.id);
        setCohortStudents(students);
      }
      loadData();
    } catch (error) {
      console.error('Delete error:', error);
      alert('Failed to delete: ' + error.message);
    }
  };

  const handleRestoreStudent = async (student) => {
    try {
      const result = await restoreDeletedStudent(student.id);
      alert(result.cohortRestored
        ? `${student.name} restored to ${result.cohortName || 'their cohort'}. They can log in again.`
        : `${student.name} restored and can log in again, but their cohort${result.cohortName ? ` (${result.cohortName})` : ''} no longer exists - use Move to put them in a cohort.`);
      loadData();
    } catch (error) {
      console.error('Restore student error:', error);
      alert('Failed to restore: ' + error.message);
    }
  };

  const handlePermanentlyDeleteStudent = async (student) => {
    const typed = window.prompt(
      `This will IMMEDIATELY and PERMANENTLY delete ${student.name} - their account, submissions, marked scripts and exam answers. This cannot be undone.\n\nType the student's name to confirm:`
    );
    if (typed === null) return; // cancelled
    if (typed.trim() !== (student.name || '').trim()) {
      alert('Name did not match. Deletion cancelled.');
      return;
    }
    try {
      await deleteStudentAccount(student.id, student.email, { permanent: true });
      alert(`${student.name} permanently deleted.`);
      loadData();
    } catch (error) {
      console.error('Permanent delete student error:', error);
      alert('Failed to delete: ' + error.message);
    }
  };

  const handleViewCohortStudents = async (cohort) => {
    setViewingCohort(cohort);
    setLoadingStudents(true);
    try {
      const students = await getCohortStudents(cohort.id);
      setCohortStudents(students);
    } catch (error) {
      console.error('Failed to load students:', error);
      alert('Failed to load students');
    } finally {
      setLoadingStudents(false);
    }
  };

  const handleViewStudentSubmissions = async (student, returnTo = 'list') => {
    setStudentReturnTo(returnTo);
    setViewingStudent(student);
    setLoadingSubmissions(true);
    try {
      const submissions = await getStudentMarkedSubmissions(student.id);
      setStudentSubmissions(submissions);
    } catch (error) {
      console.error('Failed to load submissions:', error);
      alert('Failed to load submissions');
    } finally {
      setLoadingSubmissions(false);
    }
  };

  const handleBackToCohorts = () => {
    setViewingCohort(null);
    setCohortStudents([]);
    setViewingStudent(null);
    setStudentSubmissions([]);
  };

  const handleBackToStudents = () => {
    setViewingStudent(null);
    setStudentSubmissions([]);
  };

  // Student page opened from a name in the results grid
  const handleOpenStudentFromResults = async (student, cohort) => {
    setViewingCohort(cohort);
    setEditingNameId(null);
    handleViewStudentSubmissions(student, 'hub');
    try {
      setCohortStudents(await getCohortStudents(cohort.id));
    } catch (error) {
      console.error('Failed to load cohort students:', error);
    }
  };

  const leaveStudentPage = () => {
    setEditingNameId(null);
    if (studentReturnTo === 'hub') {
      setViewingStudent(null);
      setStudentSubmissions([]);
      setViewingCohort(null);
      setCohortStudents([]);
    } else {
      handleBackToStudents();
    }
  };

  const handleSaveStudentName = async (studentId) => {
    const trimmed = editingNameValue.trim();
    if (!trimmed) return;
    try {
      await updateStudentName(studentId, trimmed);
      setCohortStudents(prev => prev.map(s => s.id === studentId ? { ...s, name: trimmed } : s));
      setEditingNameId(null);
    } catch (error) {
      alert('Failed to update name: ' + error.message);
    }
  };

  // New student management handlers
  const handleSuspendClick = (student) => {
    setSelectedStudent(student);
    setSuspensionReason('');
    setShowSuspendDialog(true);
  };

  const handleSuspendConfirm = async () => {
    if (!selectedStudent) return;
    
    setActionInProgress(true);
    try {
      await suspendStudentAccount(selectedStudent.id, suspensionReason);
      alert(`${selectedStudent.name}'s account has been suspended`);
      setShowSuspendDialog(false);
      setSelectedStudent(null);
      setSuspensionReason('');
      
      // Reload students and suspended list
      if (viewingCohort) {
        const students = await getCohortStudents(viewingCohort.id);
        setCohortStudents(students);
      }
      loadData();
    } catch (error) {
      console.error('Suspend error:', error);
      alert('Failed to suspend account: ' + error.message);
    } finally {
      setActionInProgress(false);
    }
  };

  const handleExtraTimeClick = (student) => {
    setSelectedStudent(student);
    setExtraTimeType(student.examExtraTime?.type || 'fixed');
    setExtraTimeValue(student.examExtraTime?.value != null ? String(student.examExtraTime.value) : '');
    setShowExtraTimeDialog(true);
  };

  const handleExtraTimeSave = async () => {
    if (!selectedStudent) return;
    const value = parseInt(extraTimeValue, 10);
    if (!value || value <= 0) {
      alert('Enter a positive number');
      return;
    }

    setActionInProgress(true);
    try {
      await setStudentExtraTime(selectedStudent.id, { type: extraTimeType, value });
      alert(`Extra time accommodation set for ${selectedStudent.name}`);
      setShowExtraTimeDialog(false);
      setSelectedStudent(null);

      if (viewingCohort) {
        const students = await getCohortStudents(viewingCohort.id);
        setCohortStudents(students);
      }
    } catch (error) {
      console.error('Set extra time error:', error);
      alert('Failed to set extra time: ' + error.message);
    } finally {
      setActionInProgress(false);
    }
  };

  const handleExtraTimeClear = async () => {
    if (!selectedStudent) return;
    setActionInProgress(true);
    try {
      await clearStudentExtraTime(selectedStudent.id);
      alert(`Extra time accommodation cleared for ${selectedStudent.name}`);
      setShowExtraTimeDialog(false);
      setSelectedStudent(null);

      if (viewingCohort) {
        const students = await getCohortStudents(viewingCohort.id);
        setCohortStudents(students);
      }
    } catch (error) {
      console.error('Clear extra time error:', error);
      alert('Failed to clear extra time: ' + error.message);
    } finally {
      setActionInProgress(false);
    }
  };

  const handleReactivateStudent = async (student, targetCohortId) => {
    if (!targetCohortId) {
      alert('Please select a cohort to reactivate the student into');
      return;
    }
    
    if (!window.confirm(`Reactivate ${student.name} and move them to the selected cohort?`)) {
      return;
    }
    
    setActionInProgress(true);
    try {
      await reactivateStudentAccount(student.id);
      await moveStudentToCohort(student.id, targetCohortId);
      alert(`${student.name} has been reactivated and moved to the new cohort`);
      loadData();
    } catch (error) {
      console.error('Reactivate error:', error);
      alert('Failed to reactivate account: ' + error.message);
    } finally {
      setActionInProgress(false);
    }
  };

  const handleMoveClick = (student) => {
    setSelectedStudent(student);
    setTargetCohortId('');
    setShowMoveDialog(true);
  };

  const handleMoveConfirm = async () => {
    if (!selectedStudent || !targetCohortId) {
      alert('Please select a target cohort');
      return;
    }
    
    setActionInProgress(true);
    try {
      await moveStudentToCohort(selectedStudent.id, targetCohortId);
      alert(`${selectedStudent.name} has been moved to the new cohort`);
      if (viewingStudent?.id === selectedStudent.id) leaveStudentPage();
      setShowMoveDialog(false);
      setSelectedStudent(null);
      setTargetCohortId('');
      
      // Reload students
      if (viewingCohort) {
        const students = await getCohortStudents(viewingCohort.id);
        setCohortStudents(students);
      }
      loadData();
    } catch (error) {
      console.error('Move error:', error);
      alert('Failed to move student: ' + error.message);
    } finally {
      setActionInProgress(false);
    }
  };

  const handleArchiveClick = async (student) => {
    setSelectedStudent(student);
    setActionInProgress(true);
    
    try {
      const data = await archiveStudentData(student.id);
      setArchiveData(data);
      setShowArchiveDialog(true);
    } catch (error) {
      console.error('Archive error:', error);
      alert('Failed to prepare archive: ' + error.message);
    } finally {
      setActionInProgress(false);
    }
  };

  const handleDownloadArchive = () => {
    if (!archiveData) return;
    
    const jsonString = JSON.stringify(archiveData, null, 2);
    const blob = new Blob([jsonString], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${archiveData.student.name}_archive_${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    alert('Archive downloaded! You can now safely suspend or delete this student.');
  };

  // Bulk operations handlers
  const toggleStudentSelection = (studentId) => {
    setSelectedStudents(prev => 
      prev.includes(studentId)
        ? prev.filter(id => id !== studentId)
        : [...prev, studentId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedStudents.length === cohortStudents.length) {
      setSelectedStudents([]);
    } else {
      setSelectedStudents(cohortStudents.map(s => s.id));
    }
  };

  const handleBulkDeleteConfirm = async () => {
    if (selectedStudents.length === 0) return;
    
    setActionInProgress(true);
    try {
      let deletedCount = 0;
      
      for (const studentId of selectedStudents) {
        try {
          // Get all submissions for this student
          const { getStudentSubmissions } = await import('../firebase/submissions');
          const submissions = await getStudentSubmissions(studentId);
          
          // Delete all submissions
          for (const sub of submissions) {
            await permanentlyDeleteSubmission(sub.id);
          }
          
          deletedCount++;
        } catch (error) {
          console.error(`Error deleting data for student ${studentId}:`, error);
        }
      }
      
      alert(`Successfully deleted data for ${deletedCount} student(s)`);
      setShowBulkDeleteDialog(false);
      setSelectedStudents([]);
      
      // Reload students to update any shown submissions counts
      if (viewingCohort) {
        const students = await getCohortStudents(viewingCohort.id);
        setCohortStudents(students);
      }
    } catch (error) {
      console.error('Bulk delete error:', error);
      alert('Bulk delete failed: ' + error.message);
    } finally {
      setActionInProgress(false);
    }
  };

  const handleDeleteAllCohortData = async () => {
    if (!viewingCohort) return;
    
    setActionInProgress(true);
    try {
      const { getCohortSubmissions } = await import('../firebase/submissions');
      const allSubmissions = await getCohortSubmissions(viewingCohort.id);
      
      let deletedCount = 0;
      for (const sub of allSubmissions) {
        try {
          await permanentlyDeleteSubmission(sub.id);
          deletedCount++;
        } catch (error) {
          console.error(`Error deleting submission ${sub.id}:`, error);
        }
      }
      
      alert(`Successfully deleted ${deletedCount} submission(s) from ${viewingCohort.name}`);
      setShowDeleteAllDialog(false);
      
      // Reload students
      if (viewingCohort) {
        const students = await getCohortStudents(viewingCohort.id);
        setCohortStudents(students);
      }
    } catch (error) {
      console.error('Delete all cohort data error:', error);
      alert('Delete all failed: ' + error.message);
    } finally {
      setActionInProgress(false);
    }
  };

  // The cohort hub's results grid loads its own data, so it doesn't wait
  // for the rest of this tab's (registrations, deleted lists...)
  if (loading && (screen !== 'hub' || viewingStudent)) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <div>Loading...</div>
      </div>
    );
  }

  return (
    <div>
      {/* Viewing Student Submissions */}
      {viewingStudent && (
        <div style={{
          background: 'white',
          borderRadius: '12px',
          padding: '24px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
          marginBottom: '24px'
        }}>
          <button
            onClick={leaveStudentPage}
            style={{
              padding: '8px 16px',
              background: '#e2e8f0',
              color: '#4a5568',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: '600',
              marginBottom: '16px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <ArrowLeft size={16} />
            {studentReturnTo === 'hub' ? `Back to ${viewingCohort.name}` : 'Back to Students'}
          </button>

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '6px' }}>
            {editingNameId === viewingStudent.id ? (
              <>
                <input
                  autoFocus
                  value={editingNameValue}
                  onChange={e => setEditingNameValue(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter') handleSaveStudentName(viewingStudent.id);
                    if (e.key === 'Escape') setEditingNameId(null);
                  }}
                  style={{ fontSize: '18px', padding: '4px 8px', border: '1px solid #667eea', borderRadius: '6px', width: isMobile ? '100%' : '280px' }}
                />
                <button onClick={() => handleSaveStudentName(viewingStudent.id)} title="Save name" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#27ae60', padding: '2px' }}>
                  <Check size={20} />
                </button>
                <button onClick={() => setEditingNameId(null)} title="Cancel" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#e74c3c', padding: '2px' }}>
                  <X size={20} />
                </button>
              </>
            ) : (
              <>
                <h2 style={{ margin: 0 }}>{viewingStudent.name}</h2>
                <button
                  onClick={() => { setEditingNameId(viewingStudent.id); setEditingNameValue(viewingStudent.name || ''); }}
                  title="Edit name"
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#95a5a6', padding: '2px' }}
                >
                  <Pencil size={16} />
                </button>
              </>
            )}
            {viewingStudent.suspended && (
              <span style={{ background: '#f59e0b', color: 'white', padding: '2px 8px', borderRadius: '4px', fontSize: '12px', fontWeight: '700' }}>
                SUSPENDED
              </span>
            )}
            {viewingStudent.examExtraTime && (
              <span style={{ display: 'flex', alignItems: 'center', gap: '4px', background: '#eef2ff', color: '#4338ca', padding: '2px 8px', borderRadius: '4px', fontSize: '12px', fontWeight: '700' }}>
                <Clock size={11} />
                {viewingStudent.examExtraTime.type === 'percentage'
                  ? `+${viewingStudent.examExtraTime.value}%`
                  : `+${viewingStudent.examExtraTime.value} min`}
              </span>
            )}
          </div>
          <div style={{ fontSize: '14px', color: '#7f8c8d', marginBottom: '16px', display: 'flex', flexWrap: 'wrap', gap: '4px 16px' }}>
            <span>{viewingCohort.name}</span>
            <span>{viewingStudent.email}</span>
            {viewingStudent.phone && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                <Phone size={13} />
                {viewingStudent.phone}
              </span>
            )}
          </div>

          {/* Student actions - same dialogs and confirmations as the student list */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '24px', paddingBottom: '20px', borderBottom: '1px solid #e2e8f0' }}>
            {[
              { show: true, label: 'Archive', Icon: Archive, bg: '#3b82f6', title: 'Archive student data', onClick: () => handleArchiveClick(viewingStudent) },
              { show: !viewingStudent.suspended, label: 'Suspend', Icon: Ban, bg: '#f59e0b', title: 'Suspend account', onClick: () => handleSuspendClick(viewingStudent) },
              { show: !viewingStudent.suspended, label: 'Extra Time', Icon: Clock, bg: '#818cf8', title: 'Extra time accommodation', onClick: () => handleExtraTimeClick(viewingStudent) },
              { show: !viewingStudent.suspended, label: 'Move', Icon: RefreshCw, bg: '#10b981', title: 'Move to another cohort', onClick: () => handleMoveClick(viewingStudent) },
              { show: true, label: 'Delete', Icon: Trash2, bg: '#ef4444', title: 'Delete account', onClick: () => handleDeleteStudent(viewingStudent.id, viewingStudent.name, viewingStudent.email) }
            ].filter(a => a.show).map(({ label, Icon, bg, title, onClick }) => (
              <button
                key={label}
                onClick={onClick}
                disabled={actionInProgress}
                title={title}
                style={{
                  padding: isMobile ? '8px 12px' : '10px 16px',
                  background: bg,
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: actionInProgress ? 'not-allowed' : 'pointer',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontSize: '14px'
                }}
              >
                <Icon size={14} />
                {label}
              </button>
            ))}
            {viewingStudent.suspended && (
              <div style={{ fontSize: '13px', color: '#92400e', alignSelf: 'center' }}>
                To reactivate, use Manage cohorts &gt; Suspended Students.
              </div>
            )}
          </div>

          {loadingSubmissions ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#7f8c8d' }}>
              <div style={{ fontSize: '48px', marginBottom: '16px', display: 'flex', justifyContent: 'center' }}>
                <FileText size={48} color="#bdc3c7" />
              </div>
              <div>Loading submissions...</div>
            </div>
          ) : studentSubmissions.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#7f8c8d' }}>
              <div style={{ fontSize: '48px', marginBottom: '16px', display: 'flex', justifyContent: 'center' }}>
                <FileText size={48} color="#bdc3c7" />
              </div>
              <div>No completed homeworks yet</div>
            </div>
          ) : (() => {
            const currentCohortSubs = studentSubmissions.filter(s => s.cohortId === viewingCohort.id);
            const previousCohortSubs = studentSubmissions.filter(s => s.cohortId !== viewingCohort.id);
            
            const renderSubmissionCard = (submission) => (
              <div
                key={submission.id}
                style={{
                  padding: '20px',
                  border: submission.deletedByStudent ? '2px solid #f59e0b' : '2px solid #1b998b',
                  borderRadius: '8px',
                  background: submission.deletedByStudent ? '#fef3c7' : '#f0fdf4',
                  display: 'grid',
                  gridTemplateColumns: isMobile ? '1fr' : '200px 1fr auto',
                  gap: isMobile ? '12px' : '16px',
                  alignItems: 'center',
                  opacity: submission.deletedByStudent ? 0.7 : 1
                }}
              >
                <div>
                  <div style={{ fontWeight: '700', fontSize: '18px', color: '#2d3748', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {homeworkNames[submission.homeworkType] || submission.homeworkType}
                    {submission.deletedByStudent && (
                      <span style={{
                        background: '#f59e0b',
                        color: 'white',
                        padding: '2px 8px',
                        borderRadius: '4px',
                        fontSize: '11px',
                        fontWeight: '700'
                      }}>
                        DELETED BY STUDENT
                      </span>
                    )}
                    {submission.isLate && (
                      <span style={{
                        background: '#e74c3c',
                        color: 'white',
                        padding: '2px 8px',
                        borderRadius: '4px',
                        fontSize: '11px',
                        fontWeight: '700',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}>
                        <AlertTriangle size={12} />
                        LATE
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: '12px', color: '#7f8c8d' }}>
                    {submission.homeworkType}
                  </div>
                </div>

                <div>
                  <div style={{ fontSize: '14px', color: '#7f8c8d', marginBottom: '4px' }}>
                    Submitted: {submission.submittedAt?.toDate().toLocaleDateString('en-GB')}
                  </div>
                  <div style={{ fontSize: '14px', color: '#7f8c8d' }}>
                    Marked: {submission.markedAt?.toDate().toLocaleDateString('en-GB')}
                  </div>
                  {submission.isLate && (
                    <div style={{ fontSize: '13px', color: '#e74c3c', fontWeight: '600', marginTop: '4px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <AlertTriangle size={12} />
                      {Math.floor(submission.minutesLate / 60)}h {submission.minutesLate % 60}m after deadline
                    </div>
                  )}
                  {submission.deletedByStudent && submission.deletedByStudentAt && (
                    <div style={{ fontSize: '14px', color: '#f59e0b', marginTop: '4px', fontWeight: '600' }}>
                      Deleted: {submission.deletedByStudentAt?.toDate().toLocaleDateString('en-GB')}
                    </div>
                  )}
                </div>

                <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
                  <div style={{
                    background: '#1b998b',
                    color: 'white',
                    padding: '8px 16px',
                    borderRadius: '8px',
                    fontWeight: '700',
                    fontSize: '18px'
                  }}>
                    {submission.score?.total?.toFixed(1) || '0.0'}/{maxMarks[submission.homeworkType] || 20}
                  </div>
                  <button
                    onClick={async () => {
                      try {
                        const filePath = submission.markedFilePath || submission.markedFileUrl;
                        if (!filePath) {
                          alert('Marked file not available');
                          return;
                        }
                        const url = await getAuthenticatedFileUrl(filePath);
                        if (url) {
                          window.open(url, '_blank');
                        } else {
                          alert('Unable to access file. You may not have permission.');
                        }
                      } catch (error) {
                        console.error('Error opening PDF:', error);
                        alert('Error opening PDF: ' + error.message);
                      }
                    }}
                    style={{
                      padding: '10px 20px',
                      background: '#667eea',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontWeight: '600',
                      textDecoration: 'none',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                  >
                    <FileText size={16} />
                    View Marked PDF
                  </button>
                  {submission.status === 'marked' && submission.assignedTo && !submission.deletedByStudent && (
                    <button
                      onClick={async () => {
                        const hwName = homeworkNames[submission.homeworkType] || submission.homeworkType;
                        const notes = window.prompt(`Send ${hwName} for ${viewingStudent.name} back to ${submission.markerName || 'the marker'}?\n\nIt goes back into their queue with their marking kept, and comes off the student's dashboard until it's finished again.\n\nNote for the marker (required):`);
                        if (notes === null) return;
                        if (!notes.trim()) {
                          alert('Please add a note for the marker.');
                          return;
                        }
                        try {
                          const auth = getAuth();
                          await sendMarkedBackToMarker(submission.id, notes.trim(), auth.currentUser?.displayName || auth.currentUser?.email || null);
                          try {
                            const marker = await getMarker(submission.assignedTo);
                            if (marker?.email) {
                              await fetch(CLOUD_FUNCTION_URL, await notificationRequest({
                                type: 'submission_rejected',
                                markerEmail: marker.email,
                                markerName: submission.markerName || marker.name,
                                submissionData: { studentName: viewingStudent.name, homeworkType: hwName, rejectionNotes: notes.trim() }
                              }));
                            }
                          } catch (emailError) {
                            console.error('Send-back email to marker failed:', emailError);
                          }
                          setStudentSubmissions(await getStudentMarkedSubmissions(viewingStudent.id));
                          alert(`Sent back to ${submission.markerName || 'the marker'}.`);
                        } catch (error) {
                          console.error('Send back to marker failed:', error);
                          alert('Could not send it back: ' + error.message);
                        }
                      }}
                      title="Send this marked homework back to its marker to finish or correct - their marking is kept"
                      style={{
                        padding: '10px 16px',
                        background: 'white',
                        color: '#c05621',
                        border: '2px solid #c05621',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontWeight: '600',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}
                    >
                      <Undo2 size={16} />
                      Send back to marker
                    </button>
                  )}
                  <button
                    onClick={async () => {
                      if (!window.confirm(`Permanently delete this submission?\n\nThis will remove all files from storage and cannot be undone.\n\nHomework: ${homeworkNames[submission.homeworkType] || submission.homeworkType}`)) {
                        return;
                      }
                      try {
                        await permanentlyDeleteSubmission(submission.id);
                        const updatedSubmissions = await getStudentMarkedSubmissions(viewingStudent.id);
                        setStudentSubmissions(updatedSubmissions);
                        alert('Submission permanently deleted');
                      } catch (error) {
                        console.error('Error deleting submission:', error);
                        alert('Error deleting submission: ' + error.message);
                      }
                    }}
                    style={{
                      padding: '10px 16px',
                      background: '#ef4444',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontWeight: '600',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px',
                      fontSize: '14px'
                    }}
                    title="Permanently delete this submission and all files"
                  >
                    <Trash2 size={14} />
                    Delete
                  </button>
                </div>
              </div>
            );

            return (
              <div>
                {/* Current Cohort Submissions */}
                <h3 style={{ margin: '0 0 12px 0', fontSize: '16px', color: '#2d3748' }}>
                  Current Cohort ({viewingCohort.name})
                </h3>
                {currentCohortSubs.length === 0 ? (
                  <div style={{ padding: '20px', background: '#f8f9fa', borderRadius: '8px', color: '#7f8c8d', marginBottom: '24px', textAlign: 'center' }}>
                    No homeworks completed in this cohort yet
                  </div>
                ) : (
                  <div style={{ display: 'grid', gap: '12px', marginBottom: '24px' }}>
                    {currentCohortSubs.map(renderSubmissionCard)}
                  </div>
                )}

                {/* Previous Cohort Submissions */}
                {previousCohortSubs.length > 0 && (
                  <>
                    <h3 style={{ margin: '0 0 12px 0', fontSize: '16px', color: '#95a5a6' }}>
                      Previous Cohorts
                    </h3>
                    <div style={{ display: 'grid', gap: '12px', opacity: 0.7 }}>
                      {previousCohortSubs.map(sub => (
                        <div key={sub.id} style={{ position: 'relative' }}>
                          <div style={{
                            position: 'absolute',
                            top: '8px',
                            right: '8px',
                            background: '#95a5a6',
                            color: 'white',
                            padding: '2px 10px',
                            borderRadius: '4px',
                            fontSize: '11px',
                            fontWeight: '700',
                            zIndex: 1
                          }}>
                            {sub.cohortName || 'Previous Cohort'}
                          </div>
                          {renderSubmissionCard(sub)}
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            );
          })()}
        </div>
      )}

      {/* Viewing Cohort Students (Manage cohorts > View Students) */}
      {screen === 'manage' && viewingCohort && !viewingStudent && (
        <div style={{
          background: 'white',
          borderRadius: '12px',
          padding: '24px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
          marginBottom: '24px'
        }}>
          <button
            onClick={handleBackToCohorts}
            style={{
              padding: '8px 16px',
              background: '#e2e8f0',
              color: '#4a5568',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: '600',
              marginBottom: '16px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <ArrowLeft size={16} />
            Back to Cohorts
          </button>

          <h2 style={{ margin: '0 0 8px 0' }}>{viewingCohort.name} - Students</h2>
          <div style={{ fontSize: '14px', color: '#7f8c8d', marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>{cohortStudents.length} student{cohortStudents.length !== 1 ? 's' : ''}</div>
            {cohortStudents.length > 0 && (
              <div style={{ display: 'flex', gap: '12px' }}>
                {selectedStudents.length > 0 && (
                  <button
                    onClick={() => setShowBulkDeleteDialog(true)}
                    style={{
                      padding: '8px 16px',
                      background: '#ef4444',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontWeight: '600',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      fontSize: '14px'
                    }}
                  >
                    <Trash2 size={14} />
                    Delete Selected ({selectedStudents.length})
                  </button>
                )}
                <button
                  onClick={() => setShowDeleteAllDialog(true)}
                  style={{
                    padding: '8px 16px',
                    background: '#dc2626',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: '600',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    fontSize: '14px'
                  }}
                >
                  <Trash2 size={14} />
                  Delete All Cohort Data
                </button>
              </div>
            )}
          </div>

          {loadingStudents ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#7f8c8d' }}>
              Loading students...
            </div>
          ) : cohortStudents.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#7f8c8d' }}>
              <div style={{ fontSize: '48px', marginBottom: '16px', display: 'flex', justifyContent: 'center' }}>
                <Users size={48} color="#bdc3c7" />
              </div>
              <div>No students in this cohort yet</div>
            </div>
          ) : (
            <>
              <div style={{ 
                padding: '12px 20px', 
                background: '#f7fafc', 
                borderRadius: '8px',
                marginBottom: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <input
                  type="checkbox"
                  checked={selectedStudents.length === cohortStudents.length && cohortStudents.length > 0}
                  onChange={toggleSelectAll}
                  style={{ 
                    width: '18px', 
                    height: '18px', 
                    cursor: 'pointer' 
                  }}
                />
                <label style={{ fontWeight: '600', cursor: 'pointer' }} onClick={toggleSelectAll}>
                  Select All ({selectedStudents.length}/{cohortStudents.length})
                </label>
              </div>
              <div style={{ display: 'grid', gap: '12px' }}>
              {cohortStudents.map(student => (
                <div
                  key={student.id}
                  style={{
                    padding: '20px',
                    border: `2px solid ${student.suspended ? '#fbbf24' : selectedStudents.includes(student.id) ? '#667eea' : '#e2e8f0'}`,
                    borderRadius: '8px',
                    background: student.suspended ? '#fef3c7' : selectedStudents.includes(student.id) ? '#eef2ff' : 'white',
                    display: 'grid',
                    gridTemplateColumns: '40px 1fr 250px auto',
                    gap: '16px',
                    alignItems: 'center'
                  }}
                >
                  <div>
                    <input
                      type="checkbox"
                      checked={selectedStudents.includes(student.id)}
                      onChange={() => toggleStudentSelection(student.id)}
                      onClick={(e) => e.stopPropagation()}
                      style={{ 
                        width: '20px', 
                        height: '20px', 
                        cursor: 'pointer' 
                      }}
                    />
                  </div>
                  <div>
                    <div style={{ fontWeight: '600', fontSize: '16px', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      {editingNameId === student.id ? (
                        <>
                          <input
                            autoFocus
                            value={editingNameValue}
                            onChange={e => setEditingNameValue(e.target.value)}
                            onKeyDown={e => {
                              if (e.key === 'Enter') handleSaveStudentName(student.id);
                              if (e.key === 'Escape') setEditingNameId(null);
                            }}
                            style={{ fontSize: '16px', padding: '2px 6px', border: '1px solid #667eea', borderRadius: '4px', width: '200px' }}
                          />
                          <button
                            onClick={() => handleSaveStudentName(student.id)}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#27ae60', padding: '2px' }}
                          >
                            <Check size={16} />
                          </button>
                          <button
                            onClick={() => setEditingNameId(null)}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#e74c3c', padding: '2px' }}
                          >
                            <X size={16} />
                          </button>
                        </>
                      ) : (
                        <>
                          {student.name}
                          <button
                            onClick={() => { setEditingNameId(student.id); setEditingNameValue(student.name); }}
                            title="Edit name"
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#95a5a6', padding: '2px' }}
                          >
                            <Pencil size={14} />
                          </button>
                          {student.suspended && (
                            <span style={{
                              background: '#f59e0b',
                              color: 'white',
                              padding: '2px 8px',
                              borderRadius: '4px',
                              fontSize: '12px',
                              fontWeight: '700'
                            }}>
                              SUSPENDED
                            </span>
                          )}
                          {student.examExtraTime && (
                            <span style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px',
                              background: '#eef2ff',
                              color: '#4338ca',
                              padding: '2px 8px',
                              borderRadius: '4px',
                              fontSize: '12px',
                              fontWeight: '700'
                            }}>
                              <Clock size={11} />
                              {student.examExtraTime.type === 'percentage'
                                ? `+${student.examExtraTime.value}%`
                                : `+${student.examExtraTime.value} min`}
                            </span>
                          )}
                        </>
                      )}
                    </div>
                    <div style={{ fontSize: '14px', color: '#7f8c8d' }}>
                      {student.email}
                    </div>
                  </div>

                  <div style={{ fontSize: '14px', color: '#7f8c8d', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Phone size={14} />
                    {student.phone}
                  </div>

                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <button
                      onClick={() => handleViewStudentSubmissions(student)}
                      style={{
                        padding: '10px 16px',
                        background: '#667eea',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontWeight: '600',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        fontSize: '14px'
                      }}
                    >
                      <FileText size={14} />
                      View
                    </button>
                    
                    {!student.suspended && (
                      <>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleArchiveClick(student);
                          }}
                          style={{
                            padding: '10px 16px',
                            background: '#3b82f6',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            fontWeight: '600',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            fontSize: '14px'
                          }}
                          title="Archive student data"
                        >
                          <Archive size={14} />
                          Archive
                        </button>

                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSuspendClick(student);
                          }}
                          style={{
                            padding: '10px 16px',
                            background: '#f59e0b',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            fontWeight: '600',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            fontSize: '14px'
                          }}
                          title="Suspend account"
                        >
                          <Ban size={14} />
                          Suspend
                        </button>

                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleExtraTimeClick(student);
                          }}
                          style={{
                            padding: '10px 16px',
                            background: '#818cf8',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            fontWeight: '600',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            fontSize: '14px'
                          }}
                          title="Extra time accommodation"
                        >
                          <Clock size={14} />
                          Extra Time
                        </button>

                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleMoveClick(student);
                          }}
                          style={{
                            padding: '10px 16px',
                            background: '#10b981',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            fontWeight: '600',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            fontSize: '14px'
                          }}
                          title="Move to another cohort"
                        >
                          <RefreshCw size={14} />
                          Move
                        </button>

                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteStudent(student.id, student.name, student.email);
                          }}
                          style={{
                            padding: '10px 16px',
                            background: '#ef4444',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            fontWeight: '600',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            fontSize: '14px'
                          }}
                          title="Delete account permanently"
                        >
                          <Trash2 size={14} />
                          Delete
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
            </>
          )}
        </div>
      )}

      {/* Cohorts Section (Manage cohorts) */}
      {screen === 'manage' && !viewingCohort && !viewingStudent && (
      <div style={{
        background: 'white',
        borderRadius: '12px',
        padding: '24px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
        marginBottom: '24px'
      }}>
        <button
          onClick={() => setScreen('hub')}
          style={{
            padding: '8px 16px',
            background: '#e2e8f0',
            color: '#4a5568',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontWeight: '600',
            marginBottom: '16px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          <ArrowLeft size={16} />
          Back to {hubCohort ? hubCohort.name : 'cohort'}
        </button>

        {/* View Toggle */}
        <div style={{
          display: 'flex',
          gap: '12px',
          marginBottom: '24px',
          borderBottom: '2px solid #e2e8f0',
          paddingBottom: '12px'
        }}>
          <button
            onClick={() => setCurrentView('cohorts')}
            style={{
              padding: '10px 20px',
              background: currentView === 'cohorts' ? '#c33329' : 'transparent',
              color: currentView === 'cohorts' ? 'white' : '#4a5568',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <Users size={16} />
            Active Cohorts
          </button>
          
          <button
            onClick={() => setCurrentView('suspended')}
            style={{
              padding: '10px 20px',
              background: currentView === 'suspended' ? '#c33329' : 'transparent',
              color: currentView === 'suspended' ? 'white' : '#4a5568',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              position: 'relative'
            }}
          >
            <Ban size={16} />
            Suspended Students
            {suspendedStudents.length > 0 && (
              <span style={{
                background: '#f59e0b',
                color: 'white',
                padding: '2px 8px',
                borderRadius: '12px',
                fontSize: '12px',
                fontWeight: '700'
              }}>
                {suspendedStudents.length}
              </span>
            )}
          </button>
        </div>

        {currentView === 'cohorts' && (
          <>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '24px'
            }}>
              <h2 style={{ margin: 0 }}>Student Cohorts</h2>
              {!showCreateForm && (
                <button
                  onClick={() => setShowCreateForm(true)}
                  style={{
                    padding: '12px 24px',
                    background: '#c33329',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: '600'
                  }}
                >
                  + Create New Cohort
                </button>
              )}
            </div>

        {/* Create Form */}
        {showCreateForm && (
          <form onSubmit={handleCreateCohort} style={{
            marginBottom: '32px',
            padding: '24px',
            background: '#f8f9fa',
            borderRadius: '8px',
            border: '2px solid #c33329'
          }}>
            <h3 style={{ margin: '0 0 16px 0' }}>Create New Cohort</h3>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                  Cohort Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={handleNameChange}
                  placeholder="e.g., March 2026"
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '2px solid #e2e8f0',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
              </div>
              
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                  Code (students will use this) *
                </label>
                <input
                  type="text"
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                  placeholder="EDGARATXMARCH2026"
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '2px solid #e2e8f0',
                    borderRadius: '6px',
                    fontSize: '14px',
                    textTransform: 'uppercase'
                  }}
                />
                <div style={{ marginTop: '6px', fontSize: '12px', color: '#7f8c8d' }}>
                  Codes only need to be unique within your own cohorts, but a plain code like MARCH2026 could coincidentally
                  match another tutor's cohort on this platform. Including your surname or the subject, e.g. EdgarATX-March2026, avoids that.
                </div>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                  Start Date (optional)
                </label>
                <input
                  type="date"
                  value={formData.startDate}
                  onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '2px solid #e2e8f0',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
              </div>
              
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                  End Date (optional)
                </label>
                <input
                  type="date"
                  value={formData.endDate}
                  onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '2px solid #e2e8f0',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
              </div>
            </div>

            {error && (
              <div style={{
                padding: '12px',
                background: '#fee2e2',
                border: '1px solid #fecaca',
                borderRadius: '6px',
                color: '#991b1b',
                fontSize: '14px',
                marginBottom: '16px'
              }}>
                {error}
              </div>
            )}

            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                type="submit"
                style={{
                  padding: '12px 24px',
                  background: '#c33329',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: '600'
                }}
              >
                Create Cohort
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowCreateForm(false);
                  setFormData({ name: '', code: '', startDate: '', endDate: '' });
                  setError('');
                }}
                style={{
                  padding: '12px 24px',
                  background: '#e2e8f0',
                  color: '#4a5568',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: '600'
                }}
              >
                Cancel
              </button>
            </div>
          </form>
        )}

        {/* Recently Deleted - soft-deleted cohorts, recoverable during the retention window */}
        {recentlyDeletedCohorts.length > 0 && (
          <div style={{ marginBottom: '20px', border: '2px solid #e2e8f0', borderRadius: '8px', overflow: 'hidden' }}>
            <div
              onClick={() => setShowRecentlyDeleted(!showRecentlyDeleted)}
              style={{
                padding: '14px 20px',
                background: '#f8f9fa',
                cursor: 'pointer',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                userSelect: 'none'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontWeight: '600', color: '#4a5568' }}>
                <Trash2 size={18} />
                Recently Deleted ({recentlyDeletedCohorts.length})
              </div>
              <span style={{ color: '#7f8c8d' }}>{showRecentlyDeleted ? '▼' : '▶'}</span>
            </div>
            {showRecentlyDeleted && (
              <div style={{ padding: '16px 20px', display: 'grid', gap: '12px' }}>
                {recentlyDeletedCohorts.map(cohort => {
                  const deletedAt = cohort.pendingDeletionAt?.toDate?.();
                  const daysLeft = deletedAt
                    ? Math.max(0, COHORT_DELETION_RETENTION_DAYS - Math.floor((Date.now() - deletedAt.getTime()) / (1000 * 60 * 60 * 24)))
                    : null;
                  return (
                    <div
                      key={cohort.id}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '12px 16px',
                        background: 'white',
                        border: '1px solid #e2e8f0',
                        borderRadius: '6px'
                      }}
                    >
                      <div>
                        <div style={{ fontWeight: '600' }}>{cohort.name}</div>
                        <div style={{ fontSize: '12px', color: '#7f8c8d' }}>
                          Code: {cohort.code}
                          {daysLeft !== null && ` · ${daysLeft} day${daysLeft === 1 ? '' : 's'} left before permanent deletion`}
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button
                          onClick={() => handleRestoreCohort(cohort.id, cohort.name)}
                          style={{
                            display: 'flex', alignItems: 'center', gap: '6px',
                            padding: '8px 14px', background: '#1b998b', color: 'white',
                            border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', fontSize: '13px'
                          }}
                        >
                          <RefreshCw size={14} /> Restore
                        </button>
                        <button
                          onClick={() => handlePermanentlyDeleteCohort(cohort.id, cohort.name)}
                          style={{
                            display: 'flex', alignItems: 'center', gap: '6px',
                            padding: '8px 14px', background: '#e74c3c', color: 'white',
                            border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', fontSize: '13px'
                          }}
                        >
                          <Trash2 size={14} /> Delete Permanently Now
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Recently Deleted Students - recoverable during the retention window */}
        {recentlyDeletedStudents.length > 0 && (
          <div style={{ marginBottom: '20px', border: '2px solid #e2e8f0', borderRadius: '8px', overflow: 'hidden' }}>
            <div
              onClick={() => setShowRecentlyDeletedStudents(!showRecentlyDeletedStudents)}
              style={{
                padding: '14px 20px',
                background: '#f8f9fa',
                cursor: 'pointer',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                userSelect: 'none'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontWeight: '600', color: '#4a5568' }}>
                <Trash2 size={18} />
                Recently Deleted Students ({recentlyDeletedStudents.length})
              </div>
              <span style={{ color: '#7f8c8d', display: 'flex' }}>
                {showRecentlyDeletedStudents ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
              </span>
            </div>
            {showRecentlyDeletedStudents && (
              <div style={{ padding: '16px 20px', display: 'grid', gap: '12px' }}>
                {recentlyDeletedStudents.map(student => {
                  const deletedAt = student.pendingDeletionAt?.toDate?.();
                  const daysLeft = deletedAt
                    ? Math.max(0, STUDENT_DELETION_RETENTION_DAYS - Math.floor((Date.now() - deletedAt.getTime()) / (1000 * 60 * 60 * 24)))
                    : null;
                  return (
                    <div
                      key={student.id}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: '12px',
                        flexWrap: 'wrap',
                        padding: '12px 16px',
                        background: 'white',
                        border: '1px solid #e2e8f0',
                        borderRadius: '6px'
                      }}
                    >
                      <div>
                        <div style={{ fontWeight: '600' }}>{student.name}</div>
                        <div style={{ fontSize: '12px', color: '#7f8c8d' }}>
                          {student.email}
                          {student.deletedFrom?.cohortName && ` | was in ${student.deletedFrom.cohortName}`}
                          {daysLeft !== null && ` | ${daysLeft} day${daysLeft === 1 ? '' : 's'} left before permanent deletion`}
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button
                          onClick={() => handleRestoreStudent(student)}
                          style={{
                            display: 'flex', alignItems: 'center', gap: '6px',
                            padding: '8px 14px', background: '#1b998b', color: 'white',
                            border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', fontSize: '13px'
                          }}
                        >
                          <RefreshCw size={14} /> Restore
                        </button>
                        <button
                          onClick={() => handlePermanentlyDeleteStudent(student)}
                          style={{
                            display: 'flex', alignItems: 'center', gap: '6px',
                            padding: '8px 14px', background: '#e74c3c', color: 'white',
                            border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', fontSize: '13px'
                          }}
                        >
                          <Trash2 size={14} /> Delete Permanently Now
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Cohorts List */}
        {cohorts.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#7f8c8d' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px', display: 'flex', justifyContent: 'center' }}>
              <Users size={48} color="#bdc3c7" />
            </div>
            <div>No cohorts created yet</div>
          </div>
        ) : (
          <div style={{ display: 'grid', gap: '16px' }}>
            {cohorts.map(cohort => (
              <div
                key={cohort.id}
                style={{
                  padding: '20px',
                  border: '2px solid #e2e8f0',
                  borderRadius: '8px',
                  background: cohort.active ? 'white' : '#f8f9fa'
                }}
              >
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <div>
                    <div style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
                      {cohort.name}
                      {!cohort.active && (
                        <span style={{
                          marginLeft: '12px',
                          padding: '4px 12px',
                          background: '#e2e8f0',
                          color: '#718096',
                          borderRadius: '12px',
                          fontSize: '12px',
                          fontWeight: '600'
                        }}>
                          INACTIVE
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: '14px', color: '#7f8c8d', marginBottom: '4px' }}>
                      <strong>Code:</strong> <span style={{
                        padding: '4px 12px',
                        background: '#c33329',
                        color: 'white',
                        borderRadius: '4px',
                        fontWeight: '600',
                        fontSize: '16px'
                      }}>{cohort.code}</span>
                    </div>
                    <div style={{ fontSize: '14px', color: '#7f8c8d' }}>
                      <strong>Students:</strong> {cohortStudentCounts[cohort.id] || 0}
                    </div>
                    <div style={{ marginTop: '10px' }}>
                      <div style={{ fontSize: '12px', color: '#718096', marginBottom: '4px', fontWeight: '600' }}>
                        Registration Link:
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                        <span style={{
                          fontSize: '11px',
                          color: '#4a5568',
                          background: '#f8f9fa',
                          padding: '4px 8px',
                          borderRadius: '4px',
                          border: '1px solid #e2e8f0',
                          fontFamily: 'monospace'
                        }}>
                          {PUBLIC_URL}/register?cohort={cohort.code}&org={cohort.orgId}
                        </span>
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(`${PUBLIC_URL}/register?cohort=${cohort.code}&org=${cohort.orgId}`);
                            setCopiedCohortCode(cohort.code);
                            setTimeout(() => setCopiedCohortCode(null), 2000);
                          }}
                          style={{
                            padding: '4px 10px',
                            background: copiedCohortCode === cohort.code ? '#1b998b' : '#667eea',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '12px',
                            fontWeight: '600',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {copiedCohortCode === cohort.code ? 'Copied!' : 'Copy Link'}
                        </button>
                      </div>
                    </div>
                  </div>
                  
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      onClick={() => handleViewCohortStudents(cohort)}
                      style={{
                        padding: '8px 16px',
                        background: '#667eea',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontWeight: '600',
                        fontSize: '14px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}
                    >
                      <Users size={16} />
                      View Students
                    </button>
                    <button
                      onClick={() => handleToggleActive(cohort.id, cohort.active)}
                      style={{
                        padding: '8px 16px',
                        background: cohort.active ? '#f59e0b' : '#1b998b',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontWeight: '600',
                        fontSize: '14px'
                      }}
                    >
                      {cohort.active ? 'Deactivate' : 'Activate'}
                    </button>
                    <button
                      onClick={() => handleDeleteCohort(cohort.id, cohort.name)}
                      style={{
                        padding: '8px 16px',
                        background: '#e74c3c',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontWeight: '600',
                        fontSize: '14px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}
                    >
                      <Trash2 size={16} />
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
          </>
        )}

        {/* Suspended Students View */}
        {currentView === 'suspended' && (
          <>
            <h2 style={{ margin: '0 0 24px 0' }}>Suspended Students</h2>
            
            {suspendedStudents.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#7f8c8d' }}>
                <div style={{ fontSize: '48px', marginBottom: '16px', display: 'flex', justifyContent: 'center' }}>
                  <CheckCircle size={48} color="#bdc3c7" />
                </div>
                <div>No suspended students</div>
              </div>
            ) : (
              <div style={{ display: 'grid', gap: '12px' }}>
                {suspendedStudents.map(student => (
                  <div
                    key={student.id}
                    style={{
                      padding: '20px',
                      border: '2px solid #fbbf24',
                      borderRadius: '8px',
                      background: '#fef3c7',
                      display: 'grid',
                      gridTemplateColumns: '1fr auto',
                      gap: '16px',
                      alignItems: 'center'
                    }}
                  >
                    <div>
                      <div style={{ fontWeight: '600', fontSize: '16px', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        {student.name}
                        <span style={{
                          background: '#f59e0b',
                          color: 'white',
                          padding: '2px 8px',
                          borderRadius: '4px',
                          fontSize: '12px',
                          fontWeight: '700'
                        }}>
                          SUSPENDED
                        </span>
                      </div>
                      <div style={{ fontSize: '14px', color: '#7f8c8d', marginBottom: '4px' }}>
                        {student.email} • {student.phone}
                      </div>
                      <div style={{ fontSize: '14px', color: '#7f8c8d' }}>
                        Previous Cohort: {student.cohortName || 'N/A'}
                      </div>
                      {student.suspensionReason && (
                        <div style={{ fontSize: '13px', color: '#92400e', marginTop: '8px', fontStyle: 'italic' }}>
                          Reason: {student.suspensionReason}
                        </div>
                      )}
                      <div style={{ fontSize: '12px', color: '#7f8c8d', marginTop: '4px' }}>
                        Suspended: {student.suspendedAt?.toDate().toLocaleDateString('en-GB')}
                      </div>
                    </div>

                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <select
                        onChange={(e) => {
                          if (e.target.value) {
                            handleReactivateStudent(student, e.target.value);
                            e.target.value = ''; // Reset
                          }
                        }}
                        style={{
                          padding: '10px',
                          border: '2px solid #10b981',
                          borderRadius: '8px',
                          fontSize: '14px',
                          fontWeight: '600',
                          cursor: 'pointer',
                          background: 'white'
                        }}
                      >
                        <option value="">Reactivate to Cohort...</option>
                        {cohorts.filter(c => c.active).map(cohort => (
                          <option key={cohort.id} value={cohort.id}>
                            {cohort.name}
                          </option>
                        ))}
                      </select>

                      <button
                        onClick={() => handleArchiveClick(student)}
                        style={{
                          padding: '10px 16px',
                          background: '#3b82f6',
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          fontWeight: '600',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          fontSize: '14px'
                        }}
                      >
                        <Archive size={14} />
                        Archive
                      </button>

                      <button
                        onClick={() => handleDeleteStudent(student.id, student.name, student.email)}
                        style={{
                          padding: '10px 16px',
                          background: '#ef4444',
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          fontWeight: '600',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          fontSize: '14px'
                        }}
                      >
                        <Trash2 size={14} />
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
      )}

      {/* Recent Registrations - collapsible, on Manage cohorts and at the
          top of the cohort page */}
      {!viewingStudent && ((screen === 'manage' && !viewingCohort) || (screen === 'hub' && !showHubDeadlines)) && (
      <div style={{
        background: 'white',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
        marginBottom: screen === 'hub' ? (isMobile ? '12px' : '20px') : 0,
        padding: showRegistrations ? (isMobile ? '16px' : '24px') : (isMobile ? '12px 16px' : '14px 24px')
      }}>
        {/* Collapsed by default; a red dot means someone is waiting for approval */}
        <div
          onClick={() => setShowRegistrations(open => !open)}
          role="button"
          aria-expanded={showRegistrations}
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', cursor: 'pointer', userSelect: 'none' }}
        >
          <h2 style={{ margin: 0, fontSize: '18px', display: 'flex', alignItems: 'center', gap: '10px' }}>
            Pending Registrations {loading ? '' : `(${recentRegistrations.length})`}
            {recentRegistrations.some(s => s.approvalPending === true) && (
              <span
                title="A registration is waiting for your approval"
                style={{ display: 'inline-block', width: '12px', height: '12px', borderRadius: '50%', background: '#e74c3c' }}
              />
            )}
          </h2>
          {showRegistrations ? <ChevronDown size={20} color="#7f8c8d" /> : <ChevronRight size={20} color="#7f8c8d" />}
        </div>

        {!showRegistrations ? null : recentRegistrations.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#7f8c8d' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px', display: 'flex', justifyContent: 'center' }}>
              <CheckCircle size={48} color="#bdc3c7" />
            </div>
            <div>No pending registrations to review</div>
          </div>
        ) : (
          <div style={{ display: 'grid', gap: '12px', marginTop: '16px' }}>
            {recentRegistrations.map(student => (
              <div
                key={student.id}
                style={{
                  padding: '16px',
                  border: '2px solid #f39c12',
                  borderRadius: '8px',
                  background: '#fffaf0',
                  display: 'grid',
                  gridTemplateColumns: isMobile ? '1fr' : '1fr 200px 150px 150px',
                  gap: '16px',
                  alignItems: 'center'
                }}
              >
                <div>
                  <div style={{ fontWeight: '600', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                    {student.name}
                    {student.approvalPending === true && (
                      <span style={{ fontSize: '12px', fontWeight: 700, color: '#9a3412', background: '#ffedd5', border: '1px solid #fdba74', borderRadius: '10px', padding: '2px 8px' }}>
                        Awaiting approval
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: '14px', color: '#7f8c8d' }}>
                    {student.email}
                  </div>
                  <div style={{ fontSize: '14px', color: '#7f8c8d', display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <Phone size={14} />
                    {student.phone}
                  </div>
                </div>
                
                <div style={{ fontSize: '14px', color: '#7f8c8d' }}>
                  {student.cohortName}
                </div>
                
                <div style={{ fontSize: '13px', color: '#7f8c8d', textAlign: 'right' }}>
                  {student.createdAt?.toDate ? 
                    student.createdAt.toDate().toLocaleDateString('en-GB', {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    }) : 
                    'Recently'
                  }
                </div>
                
                <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                  <button
                    onClick={() => handleApproveRegistration(student.id, student.name)}
                    style={{
                      padding: '8px 16px',
                      background: '#1b998b',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontWeight: '600',
                      fontSize: '14px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                    title="Approve this registration"
                  >
                    <Check size={16} />
                    Approve
                  </button>
                  <button
                    onClick={() => handleDeleteStudent(student.id, student.name, student.email)}
                    style={{
                      padding: '8px 16px',
                      background: '#e74c3c',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontWeight: '600',
                      fontSize: '14px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                    title="Delete this account"
                  >
                    <X size={16} />
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      )}

      {/* Cohort page (hub) - results grid for the current cohort */}
      {screen === 'hub' && !viewingStudent && (
        showHubDeadlines && hubCohort ? (
          <div style={{ display: 'grid', gap: isMobile ? '12px' : '20px' }}>
            <div>
              <button
                onClick={() => setShowHubDeadlines(false)}
                style={{ padding: '8px 16px', background: 'white', color: '#4a5568', border: '1px solid #e2e8f0', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px' }}
              >
                <ArrowLeft size={16} />
                Back to {hubCohort.name}
              </button>
            </div>
            <DeadlineManager cohortId={hubCohort.id} />
            {renderDeadlinesExtra && renderDeadlinesExtra(hubCohort)}
          </div>
        ) : (
          <ResultsView
            orgId={orgId}
            initialCohortId={hubCohort?.id}
            onCohortChange={setHubCohort}
            onStudentClick={handleOpenStudentFromResults}
            actions={
              <>
                <button
                  onClick={() => setShowHubDeadlines(true)}
                  disabled={!hubCohort}
                  style={{ padding: '10px 16px', background: '#2563eb', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '8px' }}
                >
                  <Calendar size={16} />
                  Deadlines
                </button>
                {renderCohortActions && hubCohort && renderCohortActions(hubCohort)}
                <button
                  onClick={() => setScreen('manage')}
                  style={{ padding: '10px 16px', background: '#ecf0f1', color: '#2c3e50', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '8px' }}
                >
                  <SettingsIcon size={16} />
                  Manage cohorts
                </button>
              </>
            }
            renderHeader={(cohort, { studentCount }) => {
              const registrationLink = `${PUBLIC_URL}/register?cohort=${cohort.code}&org=${cohort.orgId}`;
              return (
                <div style={{
                  border: '2px solid #e2e8f0',
                  borderRadius: '10px',
                  padding: isMobile ? '12px' : '14px 20px',
                  marginBottom: '16px',
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  gap: '10px 28px',
                  fontSize: '14px',
                  color: '#7f8c8d'
                }}>
                  <div>
                    <strong>Code:</strong>{' '}
                    <span style={{ padding: '4px 12px', background: '#c33329', color: 'white', borderRadius: '4px', fontWeight: '600', fontSize: '16px' }}>
                      {cohort.code}
                    </span>
                  </div>
                  <div><strong>Students:</strong> {studentCount}</div>
                  {!cohort.active && (
                    <span style={{ padding: '4px 12px', background: '#e2e8f0', color: '#718096', borderRadius: '12px', fontSize: '12px', fontWeight: '600' }}>
                      INACTIVE
                    </span>
                  )}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', minWidth: 0 }}>
                    <strong>Registration link:</strong>
                    {!isMobile && (
                      <span style={{ fontSize: '11px', color: '#4a5568', background: '#f8f9fa', padding: '4px 8px', borderRadius: '4px', border: '1px solid #e2e8f0', fontFamily: 'monospace', wordBreak: 'break-all' }}>
                        {registrationLink}
                      </span>
                    )}
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(registrationLink);
                        setCopiedCohortCode(cohort.code);
                        setTimeout(() => setCopiedCohortCode(null), 2000);
                      }}
                      style={{ padding: '4px 10px', background: copiedCohortCode === cohort.code ? '#1b998b' : '#667eea', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', fontWeight: '600', whiteSpace: 'nowrap' }}
                    >
                      {copiedCohortCode === cohort.code ? 'Copied!' : 'Copy Link'}
                    </button>
                  </div>
                </div>
              );
            }}
          />
        )
      )}

      {/* Suspend Dialog */}
      {showSuspendDialog && selectedStudent && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '32px',
            maxWidth: '500px',
            width: '90%'
          }}>
            <h3 style={{ margin: '0 0 16px 0' }}>Suspend Student Account</h3>
            <p style={{ marginBottom: '16px', color: '#7f8c8d' }}>
              Suspend <strong>{selectedStudent.name}</strong>? They will be able to view their work but cannot submit new homework.
            </p>
            
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
              Reason (optional):
            </label>
            <textarea
              value={suspensionReason}
              onChange={(e) => setSuspensionReason(e.target.value)}
              placeholder="e.g., Failed exam - resit required"
              style={{
                width: '100%',
                padding: '10px',
                border: '2px solid #e2e8f0',
                borderRadius: '6px',
                fontSize: '14px',
                minHeight: '80px',
                marginBottom: '24px'
              }}
            />

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowSuspendDialog(false);
                  setSelectedStudent(null);
                  setSuspensionReason('');
                }}
                disabled={actionInProgress}
                style={{
                  padding: '10px 20px',
                  background: '#e2e8f0',
                  color: '#4a5568',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: actionInProgress ? 'not-allowed' : 'pointer',
                  fontWeight: '600'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSuspendConfirm}
                disabled={actionInProgress}
                style={{
                  padding: '10px 20px',
                  background: '#f59e0b',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: actionInProgress ? 'not-allowed' : 'pointer',
                  fontWeight: '600'
                }}
              >
                {actionInProgress ? 'Suspending...' : 'Suspend Account'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Extra Time Dialog */}
      {showExtraTimeDialog && selectedStudent && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '32px',
            maxWidth: '500px',
            width: '90%'
          }}>
            <h3 style={{ margin: '0 0 16px 0' }}>Extra Time Accommodation</h3>
            <p style={{ marginBottom: '16px', color: '#7f8c8d' }}>
              Applies to every exam-mode homework <strong>{selectedStudent.name}</strong> takes. Only one type can be active at a time.
            </p>

            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
              Type:
            </label>
            <div style={{ display: 'flex', gap: '16px', marginBottom: '16px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                <input
                  type="radio"
                  checked={extraTimeType === 'fixed'}
                  onChange={() => setExtraTimeType('fixed')}
                />
                Fixed minutes
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                <input
                  type="radio"
                  checked={extraTimeType === 'percentage'}
                  onChange={() => setExtraTimeType('percentage')}
                />
                Percentage
              </label>
            </div>

            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
              {extraTimeType === 'percentage' ? 'Extra time (%)' : 'Extra time (minutes)'}:
            </label>
            <input
              type="number"
              min="1"
              value={extraTimeValue}
              onChange={(e) => setExtraTimeValue(e.target.value)}
              placeholder={extraTimeType === 'percentage' ? 'e.g. 25' : 'e.g. 15'}
              style={{
                width: '100%',
                padding: '10px',
                border: '2px solid #e2e8f0',
                borderRadius: '6px',
                fontSize: '14px',
                marginBottom: '24px'
              }}
            />

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'space-between' }}>
              {selectedStudent.examExtraTime ? (
                <button
                  onClick={handleExtraTimeClear}
                  disabled={actionInProgress}
                  style={{
                    padding: '10px 20px',
                    background: 'white',
                    color: '#ef4444',
                    border: '2px solid #ef4444',
                    borderRadius: '8px',
                    cursor: actionInProgress ? 'not-allowed' : 'pointer',
                    fontWeight: '600'
                  }}
                >
                  Clear
                </button>
              ) : <div />}

              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  onClick={() => {
                    setShowExtraTimeDialog(false);
                    setSelectedStudent(null);
                  }}
                  disabled={actionInProgress}
                  style={{
                    padding: '10px 20px',
                    background: '#e2e8f0',
                    color: '#4a5568',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: actionInProgress ? 'not-allowed' : 'pointer',
                    fontWeight: '600'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleExtraTimeSave}
                  disabled={actionInProgress}
                  style={{
                    padding: '10px 20px',
                    background: '#818cf8',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: actionInProgress ? 'not-allowed' : 'pointer',
                    fontWeight: '600'
                  }}
                >
                  {actionInProgress ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Move Dialog */}
      {showMoveDialog && selectedStudent && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '32px',
            maxWidth: '500px',
            width: '90%'
          }}>
            <h3 style={{ margin: '0 0 16px 0' }}>Move Student to Another Cohort</h3>
            <p style={{ marginBottom: '16px', color: '#7f8c8d' }}>
              Move <strong>{selectedStudent.name}</strong> from <strong>{selectedStudent.cohortName}</strong> to:
            </p>
            
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
              Select Target Cohort:
            </label>
            <select
              value={targetCohortId}
              onChange={(e) => setTargetCohortId(e.target.value)}
              style={{
                width: '100%',
                padding: '10px',
                border: '2px solid #e2e8f0',
                borderRadius: '6px',
                fontSize: '14px',
                marginBottom: '24px'
              }}
            >
              <option value="">Choose a cohort...</option>
              {cohorts.filter(c => c.active && c.id !== selectedStudent.cohortId).map(cohort => (
                <option key={cohort.id} value={cohort.id}>
                  {cohort.name}
                </option>
              ))}
            </select>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowMoveDialog(false);
                  setSelectedStudent(null);
                  setTargetCohortId('');
                }}
                disabled={actionInProgress}
                style={{
                  padding: '10px 20px',
                  background: '#e2e8f0',
                  color: '#4a5568',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: actionInProgress ? 'not-allowed' : 'pointer',
                  fontWeight: '600'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleMoveConfirm}
                disabled={actionInProgress || !targetCohortId}
                style={{
                  padding: '10px 20px',
                  background: targetCohortId ? '#10b981' : '#cbd5e0',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: (actionInProgress || !targetCohortId) ? 'not-allowed' : 'pointer',
                  fontWeight: '600'
                }}
              >
                {actionInProgress ? 'Moving...' : 'Move Student'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Archive Dialog */}
      {showArchiveDialog && selectedStudent && archiveData && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '32px',
            maxWidth: '600px',
            width: '90%',
            maxHeight: '80vh',
            overflow: 'auto'
          }}>
            <h3 style={{ margin: '0 0 16px 0' }}>Archive Student Data</h3>
            <p style={{ marginBottom: '16px', color: '#7f8c8d' }}>
              Archive for <strong>{archiveData.student.name}</strong>
            </p>
            
            <div style={{
              background: '#f8f9fa',
              borderRadius: '8px',
              padding: '16px',
              marginBottom: '24px'
            }}>
              <h4 style={{ margin: '0 0 12px 0', fontSize: '16px' }}>Summary:</h4>
              <ul style={{ margin: 0, paddingLeft: '20px' }}>
                <li>Total Submissions: {archiveData.summary.totalSubmissions}</li>
                <li>Marked: {archiveData.summary.markedSubmissions}</li>
                <li>Pending: {archiveData.summary.pendingSubmissions}</li>
                <li>Being Marked: {archiveData.summary.beingMarkedSubmissions}</li>
              </ul>
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowArchiveDialog(false);
                  setArchiveData(null);
                }}
                style={{
                  padding: '10px 20px',
                  background: '#e2e8f0',
                  color: '#4a5568',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: '600'
                }}
              >
                Close
              </button>
              <button
                onClick={handleDownloadArchive}
                style={{
                  padding: '10px 20px',
                  background: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                <FolderDown size={16} />
                Download Archive JSON
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Delete Dialog */}
      {showBulkDeleteDialog && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '32px',
            maxWidth: '500px',
            width: '90%'
          }}>
            <h3 style={{ margin: '0 0 16px 0', color: '#dc2626' }}>Delete Data for Selected Students?</h3>
            <p style={{ marginBottom: '16px', color: '#7f8c8d' }}>
              This will <strong>permanently delete all submissions and files</strong> for {selectedStudents.length} selected student{selectedStudents.length !== 1 ? 's' : ''}.
            </p>
            <p style={{ marginBottom: '24px', color: '#7f8c8d', fontWeight: '600' }}>
              This action <strong>cannot be undone</strong>. The student accounts will remain but all their homework data will be removed from storage.
            </p>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowBulkDeleteDialog(false);
                }}
                disabled={actionInProgress}
                style={{
                  padding: '10px 20px',
                  background: '#e2e8f0',
                  color: '#4a5568',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: actionInProgress ? 'not-allowed' : 'pointer',
                  fontWeight: '600'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleBulkDeleteConfirm}
                disabled={actionInProgress}
                style={{
                  padding: '10px 20px',
                  background: '#dc2626',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: actionInProgress ? 'not-allowed' : 'pointer',
                  fontWeight: '600'
                }}
              >
                {actionInProgress ? 'Deleting...' : 'Permanently Delete Data'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete All Cohort Data Dialog */}
      {showDeleteAllDialog && viewingCohort && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '32px',
            maxWidth: '500px',
            width: '90%'
          }}>
            <h3 style={{ margin: '0 0 16px 0', color: '#dc2626' }}>Delete ALL Data for {viewingCohort.name}?</h3>
            <p style={{ marginBottom: '16px', color: '#7f8c8d' }}>
              This will <strong>permanently delete ALL submissions and files</strong> for EVERY student in this cohort.
            </p>
            <p style={{ marginBottom: '24px', color: '#dc2626', fontWeight: '700', fontSize: '16px' }}>
              ⚠️ WARNING: This action CANNOT be undone and will free up storage space by removing all homework files for this cohort.
            </p>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowDeleteAllDialog(false);
                }}
                disabled={actionInProgress}
                style={{
                  padding: '10px 20px',
                  background: '#e2e8f0',
                  color: '#4a5568',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: actionInProgress ? 'not-allowed' : 'pointer',
                  fontWeight: '600'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteAllCohortData}
                disabled={actionInProgress}
                style={{
                  padding: '10px 20px',
                  background: '#dc2626',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: actionInProgress ? 'not-allowed' : 'pointer',
                  fontWeight: '600'
                }}
              >
                {actionInProgress ? 'Deleting All...' : 'Delete All Cohort Data'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CohortManagement;